import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MultiIdCombobox } from '@/components/ui/multi-id-combobox';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { ITEM_TYPES } from 'constants/itemType';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  NotificationPriority,
  NotificationType,
  useCreateNotification,
} from 'hooks/useNotification';
import { buildResourceRequestOpenAppMessage } from 'components/MapPage/CorporateViewTabs/NotificationsTab/appMessageTaskLinks';

type Ics213RrRouteDialogProps = {
  open: boolean;
  title: string;
  requestId: string;
  /** Request number (or ICS-213RR fallback) shown as the in-app link label. */
  requestLabel: string;
  onOpenChange: (open: boolean) => void;
};

/** Pick a roster user or ICS position and send an in-app link to this 213RR. */
export function Ics213RrRouteDialog({
  open,
  title,
  requestId,
  requestLabel,
  onOpenChange,
}: Ics213RrRouteDialogProps) {
  const incidentId = useActualIncidentId();
  const { data: roster = [] } = useIncidentRoster();
  const { data: positions = [] } = useGetPositionsForIncident();
  const notify = useCreateNotification();
  const [userIds, setUserIds] = useState<string[]>([]);
  const [positionIds, setPositionIds] = useState<string[]>([]);

  const userOptions = useMemo(
    () =>
      roster
        .filter((member) => member.active !== 0 && member.userId)
        .map((member) => ({
          value: member.userId,
          label: member.userName || member.emailAddress || member.userId,
        })),
    [roster]
  );

  const positionOptions = useMemo(
    () =>
      positions
        .filter((position) => !position.isResourcePosition)
        .map((position) => ({
          value: position.id,
          label: position.name,
        })),
    [positions]
  );

  const send = async () => {
    const fromPositions = roster
      .filter((member) =>
        member.incidentPositions.some((position) =>
          positionIds.includes(position.positionId)
        )
      )
      .map((member) => member.userId);
    const recipients = [...new Set([...userIds, ...fromPositions])].filter(
      Boolean
    );
    if (!incidentId || recipients.length === 0) return;
    const path = `/iap/${incidentId}?openTab=Resources&openRequest=${requestId}`;
    await notify.mutateAsync({
      incidentId,
      acknowledgementRequired: false,
      autoArchiveDate: null,
      priority: NotificationPriority.INFO,
      emailSubject: null,
      emailMessage: null,
      smsMessage: null,
      teamsMessage: null,
      phoneMessage: null,
      appMessage: buildResourceRequestOpenAppMessage({
        prefix: `${title}.`,
        itemName: requestLabel,
        path,
      }),
      userIds: recipients,
      teamIds: [],
      sourceItemId: requestId,
      sourceItemType: ITEM_TYPES.ResourceRequest,
      scheduledDate: null,
      notificationTypes: [NotificationType.IN_APP],
      aors: [],
    });
    setUserIds([]);
    setPositionIds([]);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark tw:sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="tw:flex tw:flex-col tw:gap-3">
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Users
            </div>
            <MultiIdCombobox
              options={userOptions}
              value={userIds}
              onChange={setUserIds}
              placeholder="Select users…"
            />
          </div>
          <div>
            <div className="tw:mb-1 tw:text-xs tw:font-medium tw:text-muted-foreground">
              Positions
            </div>
            <MultiIdCombobox
              options={positionOptions}
              value={positionIds}
              onChange={setPositionIds}
              placeholder="Select positions…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={notify.isPending || (!userIds.length && !positionIds.length)}
            onClick={() => void send()}
          >
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
